// The category index (PRD AC3, rev. 1.12): which project code each Outlook category name means. The
// app recategorizes every entry to its code ("AN001 · Andela") and keeps the person's own category for
// reference only. The SharePoint Project Categories Index list pre-fills it: opening the screen with
// an empty index reads the list, finds its columns and shows what it would add. Categories people use
// that the index can't place are listed to add. Saving recounts every unresolved entry.

import { useEffect, useMemo, useState } from "react";
import { Body1, Button, Caption1, Card, Input, Select, Subtitle2 } from "@fluentui/react-components";
import { categoryKey, guessListColumns, newId, planCopy, type CategoryMapping, type SkipReason } from "../../../core";
import { api, errorText, send, useApi, type CategoryIndexResponse, type CategoryIndexSaved, type CategoryListResponse } from "../api";
import { useCodeLabels } from "../codes";
import { href } from "../router";
import { ErrorBar, Loading, Page, useMe, Warnings } from "../ui";

interface InUse {
  category: string;
  entries: number;
  hours: number;
  code: string | null;
}

const SKIP_TEXT: Record<SkipReason, string> = {
  blank: "No category name",
  "no-code": "No code in the code column",
  "unknown-code": "Code isn't on the Projects list",
  duplicate: "Already in the index",
};

export function CategoryIndex() {
  const me = useMe();
  const editable = me.can("period.close");
  const index = useApi<CategoryIndexResponse>("/category-index");
  const inUse = useApi<{ categories: InUse[] }>("/actuals/categories");
  const { label, options, known: knownCodes, ready } = useCodeLabels();
  const known = useMemo(() => new Set(knownCodes), [knownCodes.size]); // eslint-disable-line react-hooks/exhaustive-deps
  const [rows, setRows] = useState<CategoryMapping[]>([]);
  const [etag, setEtag] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState({ category: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [list, setList] = useState<CategoryListResponse | null>(null);
  const [columns, setColumns] = useState({ category: "", code: "" });
  const [autoRead, setAutoRead] = useState(false);
  const [unmappedCodes, setUnmappedCodes] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!index.data) return;
    setRows(index.data.mappings);
    setEtag(index.data.etag);
    setDirty(false);
  }, [index.data]);

  // An empty index pre-fills from the SharePoint list as soon as the screen opens.
  useEffect(() => {
    if (editable && ready && index.data && !index.data.mappings.length && !autoRead) {
      setAutoRead(true);
      void readList();
    }
  }, [editable, ready, index.data, autoRead]);

  const mapped = new Set(rows.map((r) => categoryKey(r.category)));
  const shown = rows.filter((r) => !search || `${r.category} ${label(r.code)}`.toLowerCase().includes(search.toLowerCase()));
  const duplicate = draft.category.trim() && mapped.has(categoryKey(draft.category));
  const unplaced = (inUse.data?.categories ?? []).filter((c) => !c.code && !mapped.has(categoryKey(c.category)));

  const edit = (next: CategoryMapping[]) => {
    setRows([...next].sort((a, b) => a.category.localeCompare(b.category)));
    setDirty(true);
    setMessage(undefined);
  };

  async function save(next: CategoryMapping[], copiedFromList = false) {
    setBusy(true);
    setError(undefined);
    try {
      const r = await send<CategoryIndexSaved>("PUT", "/category-index", { etag, mappings: next, copiedFromList });
      setRows(r.mappings);
      setEtag(r.etag);
      setDirty(false);
      setList(null);
      setMessage(`Saved ${r.mappings.length} rows. ${r.recounted ? `${r.recounted} entries were recategorized.` : "No entries needed recategorizing."}`);
      inUse.reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  async function readList() {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api<CategoryListResponse>("/category-index/read-list", { method: "POST" });
      setList(r);
      setColumns(guessListColumns(r.columns, r.rows, known));
    } catch (err) {
      setError(`Couldn't read the SharePoint list: ${errorText(err)}`);
    }
    setBusy(false);
  }

  const plan = useMemo(() => (list && columns.category && columns.code ? planCopy(list.rows, columns, known, rows, newId) : null), [list, columns, known, rows]);
  const skippedBy = useMemo(() => {
    const out = new Map<SkipReason, { category: string; value: string }[]>();
    for (const s of plan?.skipped ?? []) out.set(s.reason, [...(out.get(s.reason) ?? []), s]);
    return out;
  }, [plan]);

  return (
    <Page title="Category index">
      <a href={href("actuals")} className="back">
        ← Actuals
      </a>
      <ErrorBar message={index.error ?? inUse.error ?? error} />
      {message && <Warnings intent="info" items={[message]} />}

      {editable && (list || busy) && (
        <Card className="panel wide">
          <Subtitle2>From the SharePoint list</Subtitle2>
          {!list ? (
            <Loading label="Reading the Project Categories Index list…" />
          ) : (
            <>
              <Caption1>
                The list has {list.rows.length} rows. These columns were picked automatically; change them if they're wrong.
              </Caption1>
              <div className="toolbar">
                {(["category", "code"] as const).map((k) => (
                  <label key={k} className="inline-field">
                    <Caption1>{k === "category" ? "Category in Outlook" : "Project code"}</Caption1>
                    <Select size="small" value={columns[k]} onChange={(_, x) => setColumns((c) => ({ ...c, [k]: x.value }))} aria-label={`${k} column`}>
                      <option value="">Pick a column</option>
                      {list.columns.map((c) => (
                        <option key={c.name} value={c.name}>
                          {c.displayName}
                        </option>
                      ))}
                    </Select>
                  </label>
                ))}
              </div>
              {plan && (
                <>
                  <Body1>
                    <strong>{plan.add.length}</strong> rows to add.
                  </Body1>
                  {plan.add.length > 0 && (
                    <div className="table-scroll short">
                      <table className="edit-table compact">
                        <thead>
                          <tr>
                            <th>Category in Outlook</th>
                            <th>Recategorized to</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.add.slice(0, 200).map((m) => (
                            <tr key={m.id}>
                              <td>{m.category}</td>
                              <td>{label(m.code)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {[...skippedBy.entries()].map(([reason, list]) => (
                    <Caption1 key={reason} className="block">
                      {SKIP_TEXT[reason]}: {list.length} ({list.slice(0, 6).map((s) => s.category || s.value).join("; ")}
                      {list.length > 6 ? "; …" : ""})
                    </Caption1>
                  ))}
                  <div className="toolbar">
                    <Button appearance="primary" disabled={busy || !plan.add.length || dirty} onClick={() => void save([...rows, ...plan.add], true)}>
                      {`Add ${plan.add.length} rows and recategorize`}
                    </Button>
                    <Button onClick={() => setList(null)}>Not now</Button>
                    {dirty && <Caption1 className="muted">Save or undo your edits below first.</Caption1>}
                  </div>
                </>
              )}
            </>
          )}
        </Card>
      )}

      {editable && unplaced.length > 0 && (
        <Card className="panel wide">
          <Subtitle2>Categories in use that the index can't place</Subtitle2>
          <Caption1>People's own category names with no code yet. Pick the code each means and add it; saving recategorizes their entries.</Caption1>
          <div className="table-scroll short">
            <table className="edit-table compact">
              <thead>
                <tr>
                  <th>Category in Outlook</th>
                  <th className="num">Entries</th>
                  <th className="num">Hours</th>
                  <th>Means</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {unplaced.map((c) => (
                  <tr key={c.category}>
                    <td>{c.category}</td>
                    <td className="num">{c.entries}</td>
                    <td className="num">{c.hours}</td>
                    <td>
                      <Select size="small" value={unmappedCodes[c.category] ?? ""} onChange={(_, x) => setUnmappedCodes((m) => ({ ...m, [c.category]: x.value }))} aria-label={`Code for ${c.category}`}>
                        <option value="">Pick a code</option>
                        {options.map((o) => (
                          <option key={o.code} value={o.code}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td>
                      <Button size="small" disabled={!unmappedCodes[c.category]} onClick={() => edit([...rows, { id: newId(), category: c.category, code: unmappedCodes[c.category] }])}>
                        Add
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dirty && <Caption1>Added rows take effect when you save the index below.</Caption1>}
        </Card>
      )}

      <Card className="panel wide">
        <Caption1>
          Each row says which code a category in Outlook means; matching ignores capitals and spaces at either end. Entries are recategorized to
          that code automatically, and the person's own category is kept for reference only. A category that already starts with a code
          ("AN001 - Andela") needs no row.
        </Caption1>
        {index.loading ? (
          <Loading />
        ) : (
          <>
            {editable && (
              <div className="toolbar">
                <Input size="small" placeholder="Category as it appears in Outlook" value={draft.category} onChange={(_, x) => setDraft((d) => ({ ...d, category: x.value }))} aria-label="New category" list="categories-in-use" />
                <datalist id="categories-in-use">
                  {(inUse.data?.categories ?? []).filter((c) => !mapped.has(categoryKey(c.category))).map((c) => (
                    <option key={c.category} value={c.category} />
                  ))}
                </datalist>
                <Select size="small" value={draft.code} onChange={(_, x) => setDraft((d) => ({ ...d, code: x.value }))} aria-label="Code for the new category">
                  <option value="">Pick a code</option>
                  {options.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <Button
                  size="small"
                  disabled={!draft.category.trim() || !draft.code || !!duplicate}
                  onClick={() => {
                    edit([...rows, { id: newId(), category: draft.category.trim(), code: draft.code }]);
                    setDraft({ category: "", code: "" });
                  }}
                >
                  Add
                </Button>
                {duplicate && <Caption1 className="error-text">That category is already in the index.</Caption1>}
              </div>
            )}
            <div className="toolbar">
              <Input size="small" placeholder="Search" value={search} onChange={(_, x) => setSearch(x.value)} aria-label="Search the index" />
              <Caption1 className="muted">
                {rows.length} rows{index.data?.copiedFromListAt ? `; pre-filled from SharePoint on ${index.data.copiedFromListAt.slice(0, 10)}` : ""}.
              </Caption1>
              {editable && (
                <>
                  <Button appearance="primary" disabled={!dirty || busy} onClick={() => void save(rows)}>
                    {busy ? "Saving…" : "Save and recategorize"}
                  </Button>
                  {!list && (
                    <Button appearance="subtle" disabled={busy} onClick={() => void readList()}>
                      Read the SharePoint list again
                    </Button>
                  )}
                </>
              )}
            </div>
            {rows.length === 0 ? (
              <Body1>No rows yet.</Body1>
            ) : (
              <div className="table-scroll">
                <table className="edit-table">
                  <thead>
                    <tr>
                      <th>Category in Outlook</th>
                      <th>Recategorized to</th>
                      {editable && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => (
                      <tr key={r.id}>
                        <td>{r.category}</td>
                        <td>
                          {editable ? (
                            <Select size="small" value={r.code} onChange={(_, x) => edit(rows.map((y) => (y.id === r.id ? { ...y, code: x.value } : y)))} aria-label={`Code for ${r.category}`}>
                              {!known.has(r.code) && <option value={r.code}>{r.code} (not on the Projects list)</option>}
                              {options.map((o) => (
                                <option key={o.code} value={o.code}>
                                  {o.label}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            label(r.code)
                          )}
                        </td>
                        {editable && (
                          <td>
                            <Button size="small" appearance="subtle" onClick={() => edit(rows.filter((y) => y.id !== r.id))}>
                              Remove
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>
    </Page>
  );
}
